import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info, CheckIcon } from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { useNotification } from "@/context/NotificationContext";
import {
  adminCreateUserSchema,
  type AdminCreateUserFormData,
} from "@/modules/hub/features/UserManagement/schemas/userSchemas";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EntitySingleSelect } from "@/components/EntitySingleSelect/EntitySingleSelect";
import { fetchCompanies } from "@/api/fetchCompanies/fetchCompaniesApi";
import { fetchAdminCreateUserFormDetails } from "../../api/fetchAdminCreateUserFormDetails";
import { AdminCreateUserFormDetails } from "../../api/fetchAdminCreateUserFormDetails/types";
import { createAdminUser } from "../../api/createAdminUser";

// Extended entity type for companies
interface CompanyEntity {
  id: string;
  name: string;
  isCertainPath: boolean;
}

const AdminCreateUser: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [saveLoading, setSaveLoading] = useState(false);
  const [formDetails, setFormDetails] =
    useState<AdminCreateUserFormDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyEntity | null>(
    null,
  );

  const form = useForm<AdminCreateUserFormData>({
    resolver: zodResolver(adminCreateUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      businessRoleId: undefined,
      companyUuid: "",
      sendWelcomeEmail: false,
    },
    mode: "onChange",
  });

  const { control, handleSubmit, formState, watch, setValue } = form;
  const { isSubmitting, isValid } = formState;
  const selectedRoleId = watch("businessRoleId");

  useEffect(() => {
    const fetchFormDetails = async () => {
      try {
        setLoading(true);
        const response = await fetchAdminCreateUserFormDetails();
        setFormDetails(response.data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load form details",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchFormDetails();
  }, []);

  const onSubmit = async (values: AdminCreateUserFormData) => {
    try {
      setSaveLoading(true);
      await createAdminUser({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        businessRoleId: values.businessRoleId,
        companyUuid: values.companyUuid,
        sendWelcomeEmail: values.sendWelcomeEmail,
      });
      showNotification(
        "Successfully created user!",
        values.sendWelcomeEmail
          ? "The new user has been created and will receive a password setup email once provisioned."
          : "The new user has been created.",
        "success",
      );
      navigate("/admin/users");
    } catch {
      // Error is handled by axios interceptor
    } finally {
      setSaveLoading(false);
    }
  };

  const availableRoles = formDetails?.availableRoles || [];

  // Filter roles based on selected company
  // If CertainPath company → only show CP-only roles
  // If non-CP company → only show non-CP-only roles
  const filteredRoles = useMemo(() => {
    if (!selectedCompany) return [];
    if (selectedCompany.isCertainPath) {
      return availableRoles.filter((role) => role.isCertainPathOnly);
    }
    return availableRoles.filter((role) => !role.isCertainPathOnly);
  }, [availableRoles, selectedCompany]);

  const selectedRole =
    filteredRoles.find((r) => r.id === selectedRoleId) || null;

  // Clear role selection if it's no longer valid for the selected company
  useEffect(() => {
    if (selectedRoleId && !filteredRoles.find((r) => r.id === selectedRoleId)) {
      setValue("businessRoleId", undefined as unknown as number);
    }
  }, [filteredRoles, selectedRoleId, setValue]);

  // Fetch companies for EntitySingleSelect - use 100 per page like CompanySwitcherModal
  const fetchCompaniesForSelect = useCallback(
    async ({
      searchTerm,
      page,
    }: {
      searchTerm?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const result = await fetchCompanies({
        searchTerm,
        page,
        pageSize: 100,
        sortBy: "companyName",
        sortOrder: "ASC",
      });
      const { companies } = result.data;
      const totalCount = result.meta?.totalCount || companies.length;
      return {
        data: companies.map((c) => ({
          id: c.uuid,
          name: c.companyName,
          isCertainPath: c.isCertainPath || false,
        })),
        totalCount,
      };
    },
    [],
  );

  // Handle company selection
  const handleCompanyChange = useCallback(
    (company: CompanyEntity | null) => {
      setSelectedCompany(company);
      setValue("companyUuid", company?.id || "", { shouldValidate: true });
    },
    [setValue],
  );

  // Custom row renderer to show CP badge
  const renderCompanyRow = useCallback(
    (
      company: CompanyEntity,
      isSelected: boolean,
      toggle: (ent: CompanyEntity) => void,
    ) => (
      <button
        className={`w-full px-3 py-2 text-left transition-colors ${
          isSelected
            ? "bg-gray-100 dark:bg-gray-800"
            : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
        }`}
        onClick={() => toggle(company)}
        type="button"
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <span className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
              {company.name}
            </span>
            {company.isCertainPath && (
              <span className="text-[10px] bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 px-1.5 py-0.5 rounded-full flex-shrink-0">
                CP
              </span>
            )}
          </div>
          {isSelected && (
            <CheckIcon className="h-3.5 w-3.5 text-primary flex-shrink-0 ml-2" />
          )}
        </div>
      </button>
    ),
    [],
  );

  return (
    <MainPageWrapper
      error={error}
      loading={loading || saveLoading}
      title="Create User"
    >
      <Form {...form}>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="text-sm text-muted-foreground mb-2">
            Fields marked with an asterisk (*) are required.
          </div>
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter first name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter email address"
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="companyUuid"
            render={() => (
              <FormItem>
                <FormLabel>Company *</FormLabel>
                <FormControl>
                  <EntitySingleSelect<CompanyEntity>
                    entityNamePlural="Companies"
                    entityNameSingular="Company"
                    fetchEntities={fetchCompaniesForSelect}
                    onChange={handleCompanyChange}
                    placeholder="Select a company..."
                    renderEntityRow={renderCompanyRow}
                    value={selectedCompany}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="businessRoleId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Business Role *</FormLabel>
                <Select
                  disabled={!selectedCompany}
                  onValueChange={(value) => {
                    const parsed = parseInt(value, 10);
                    if (!isNaN(parsed)) {
                      field.onChange(parsed);
                    }
                  }}
                  value={field.value ? field.value.toString() : undefined}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          selectedCompany
                            ? "Select a role..."
                            : "Select a company first..."
                        }
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {filteredRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id.toString()}>
                        {role.label}
                        {role.isCertainPathOnly && (
                          <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300 px-2 py-0.5 rounded-full">
                            CP
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedRole?.description && (
                  <div className="flex items-start gap-2 mt-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700">
                    <Info className="h-4 w-4 text-slate-500 dark:text-slate-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {selectedRole.description}
                    </p>
                  </div>
                )}
                {selectedRole?.permissions &&
                  selectedRole.permissions.length > 0 && (
                    <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                        This role includes:
                      </p>
                      <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
                        {selectedRole.permissions.map((permission) => (
                          <li
                            className="flex items-center gap-2"
                            key={permission.id}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500" />
                            {permission.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="sendWelcomeEmail"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start gap-3 rounded-md border border-gray-200 dark:border-slate-700 p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Send password setup email</FormLabel>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Emails the user an Auth0 link to set their password once
                    their account is provisioned. Leave unchecked to add them
                    without an invite.
                  </p>
                </div>
              </FormItem>
            )}
          />
          <div className="flex justify-end gap-4 pt-4">
            <Button
              onClick={() => navigate("/admin/users")}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={!isValid || isSubmitting || saveLoading}
              type="submit"
            >
              {isSubmitting || saveLoading ? "Creating..." : "Create User"}
            </Button>
          </div>
        </form>
      </Form>
    </MainPageWrapper>
  );
};

export default AdminCreateUser;
